import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, EXTERNAL_LINKS } from "@/lib/site-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-b border-border/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-24 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center group">
          <img
            src="/studyvia-logo.png"
            alt="Studyvia — Guiding Dreams Beyond Borders"
            className="h-20 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`relative text-sm font-medium whitespace-nowrap hover:text-primary transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-300 text-foreground`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={EXTERNAL_LINKS.msmunify}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ext-msmunify"
            className={`text-xs uppercase tracking-wider whitespace-nowrap hover:text-primary transition-colors flex items-center gap-1 text-foreground/80`}
          >
            MSM Unify <ArrowUpRight size={13} />
          </a>
          <a
            href={EXTERNAL_LINKS.careerbanale}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ext-careerbanale"
            className={`text-xs uppercase tracking-wider whitespace-nowrap hover:text-primary transition-colors flex items-center gap-1 text-foreground/80`}
          >
            Career Banale <ArrowUpRight size={13} />
          </a>
          <a
            href="#contact"
            data-testid="header-cta"
            className="ml-2 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors duration-300"
          >
            Book a Consultation
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 transition-colors text-secondary`}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-border overflow-hidden"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base text-foreground/80"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-4 pt-2">
              <a href={EXTERNAL_LINKS.msmunify} target="_blank" rel="noopener noreferrer" className="text-sm text-primary">MSM Unify ↗</a>
              <a href={EXTERNAL_LINKS.careerbanale} target="_blank" rel="noopener noreferrer" className="text-sm text-primary">Career Banale ↗</a>
            </div>
            <a href="#contact" onClick={() => setOpen(false)} className="mt-2 text-center rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground">
              Book a Consultation
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
